import { useEffect, useState } from 'react';

/**
 * Extra @-mention sources: documents, boards, database connections
 * (18.08.2026).
 *
 * The picker offered collections, requests and environments — three of the six
 * surfaces the assistant has tools for. A user could not point at the document
 * they were discussing or the board they wanted filled in, even though the
 * model has tools for both; they had to describe it in prose and hope the
 * model's own lookup found the right one.
 *
 * Loaded LAZILY, on first open of the picker. These reads hit extension
 * storage and, for databases, saved-connection files — doing that on every
 * panel mount would cost work for a picker most turns never open. They are
 * cached for the panel's lifetime afterwards: the lists are small and change
 * rarely, and a picker that stalls on each keystroke is worse than one showing
 * a board added a minute ago.
 */
export function useMentionSources({ enabled, extensionActions }) {
  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (!enabled || sources.length > 0 || !extensionActions) return undefined;
    let stale = false;

    (async () => {
      const items = [];

      // Each source is independent — a missing or failing extension must not
      // take the others down with it, so no Promise.all short-circuit here.
      const safely = async (fn) => {
        try { return await fn(); } catch { return null; }
      };

      const [docs, boards, connections] = await Promise.all([
        safely(() => extensionActions.get_documents?.({})),
        safely(() => extensionActions.get_boards?.({})),
        safely(() => extensionActions.db_list_connections?.({})),
      ]);

      for (const d of docs?.documents || []) {
        if (!d?.id) continue;
        items.push({
          type: 'document',
          id: String(d.id),
          label: d.title || d.name || 'Untitled document',
          meta: 'Document',
          data: { id: d.id, title: d.title || d.name },
        });
      }

      for (const b of boards?.boards || []) {
        if (!b?.id) continue;
        items.push({
          type: 'board',
          id: String(b.id),
          label: b.name || 'Untitled board',
          meta: typeof b.task_count === 'number' ? `Board · ${b.task_count} tasks` : 'Board',
          data: { id: b.id, name: b.name, task_count: b.task_count },
        });
      }

      for (const c of connections?.connections || []) {
        const id = c?.conn_id ?? c?.connId ?? c?.id;
        if (!id) continue;
        items.push({
          type: 'db_connection',
          id: String(id),
          label: c.name || c.database || String(id),
          meta: c.engine ? `Database · ${c.engine}` : 'Database',
          data: { conn_id: id, name: c.name, engine: c.engine, database: c.database },
        });
      }

      if (!stale) setSources(items);
    })();

    return () => { stale = true; };
  }, [enabled, extensionActions, sources.length]);

  return sources;
}

/** Render one of the new reference types into the prompt's context block. */
export function describeMention(ref) {
  if (ref.type === 'document') {
    return `- Document "${ref.label}" (document_id: ${ref.id}) — call read_document for its contents`;
  }
  if (ref.type === 'board') {
    const count = ref.data?.task_count;
    return `- Board "${ref.label}" (board_id: ${ref.id}${typeof count === 'number' ? `, ${count} tasks` : ''}) — call get_board to read it`;
  }
  if (ref.type === 'db_connection') {
    const engine = ref.data?.engine ? `, ${ref.data.engine}` : '';
    return `- Database connection "${ref.label}" (conn_id: ${ref.id}${engine}) — call db_get_schema before writing SQL`;
  }
  return null;
}
